import { useCallback, useEffect, useRef, useState } from "react";
import type { SearchPanelResult } from "../../molecules/search-panel/search-panel";
import { toProductResults, toSupportResults } from "./search-suggest.adapter";
import {
  MAX_SUGGESTIONS,
  MAX_SUPPORT_SUGGESTIONS,
  PRODUCT,
  requestSuggestions,
  SUPPORT
} from "./search-suggest.request";

/** OQ-3 — assumed. Below this a suggest is noise, not help. */
export const MIN_QUERY_LENGTH = 2;

export interface SearchSuggestionsState {
  /** ⚠ `undefined` is "not answered yet"; `[]` is "nothing matched". */
  results?: SearchPanelResult[];
  loading: boolean;
  error: boolean;
  /**
   * The failed request's HTTP status as a string, or `"unknown"` when there
   * was none to read (network failure, malformed body). Set only with `error`.
   */
  errorCode?: string;
  /** Hand straight to NavSearch's `onQueryChange` — it arrives already debounced. */
  onQueryChange: (query: string) => void;
}

/**
 * Owns the fetch NavSearch deliberately does not do. NavSearch debounces on
 * the way out, so this receives an already-settled term and issues one call
 * per query, aborting any predecessor still in flight.
 *
 * `error` is a boolean, not copy: the failure message is an open UX question
 * (HIM-4761, OQ-12), and baking placeholder text into the design system would
 * be the wrong call here. `NavSearchLive` maps the boolean to the host's copy.
 *
 * ⚠ A page does not call this: it renders `NavSearchLive`, which does, so the
 * state stays in the client leaf and the page stays a Server Component.
 *
 * ⚠ `suggestBase` has no default, and must NOT carry the app's base path:
 * the suggesters are site-level, not under `/uf`, the same reason the search
 * form's action has none. It stays relative so each environment answers from
 * its own host. For example:
 *
 * ```tsx
 * <NavSearchLive
 *   panelId={SEARCH_PANEL_ID}
 *   action="/searchResults"
 *   name="keyword"
 *   suggestBase="/searchResults/api"
 *   unavailableMessage={copy.searchUnavailable}
 * />
 * ```
 */
export function useSearchSuggestions(suggestBase: string): SearchSuggestionsState {
  const [results, setResults] = useState<SearchPanelResult[] | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorCode, setErrorCode] = useState<string>();

  /* One in-flight request at a time: a slow answer for "nf" must never paint
     over a fast answer for "nfl". */
  const controllerRef = useRef<AbortController>();
  /* The term in flight or answered. Cleared on failure, so retyping a failed
     term retries it while a whitespace-only edit of a good one does not refetch. */
  const lastTermRef = useRef<string>();

  useEffect(
    () => () => {
      controllerRef.current?.abort();
      /* Orphaned, not superseded — nothing will ever check isCurrent() again
         for this hook instance, but clearing it keeps the ref's invariant
         ("names the one attempt whose result matters, or nothing") true on
         every exit rather than on only the happy one. */
      controllerRef.current = undefined;
    },
    []
  );

  const onQueryChange = useCallback(
    (query: string) => {
      const term = query.trim();
      if (term.length >= MIN_QUERY_LENGTH && term === lastTermRef.current) {
        return;
      }
      controllerRef.current?.abort();
      lastTermRef.current = undefined;

      if (term.length < MIN_QUERY_LENGTH) {
        /* Back to "not answered yet" rather than "nothing matched", so the
           panel shows its resting state instead of the empty-results message. */
        setResults(undefined);
        setLoading(false);
        setError(false);
        setErrorCode(undefined);
        /* This controller is aborted but ORPHANED, not superseded: nothing
           replaces it on the ref. `.abort()` has no effect on a request that
           already fulfilled, so a stale `.then()` queued before this call can
           still run after it. Clearing the ref is what makes its `isCurrent()`
           read false so that late, successful answer can't paint under an
           emptied box. */
        controllerRef.current = undefined;
        return;
      }

      const controller = new AbortController();
      controllerRef.current = controller;
      lastTermRef.current = term;
      setLoading(true);
      setError(false);
      setErrorCode(undefined);
      /* The status of a non-OK answer, kept for `errorCode`; anything else
         that fails has no backend status to report. */
      let failedStatus: string | undefined;

      /* A superseding call, a clear, and unmount all abort THIS controller
         AND clear (or replace) the ref, so a stale settle recognises "I was
         superseded/orphaned" (ref has moved on or is empty — whoever owns the
         ref now, if anyone, already owns loading/results/error, so stay
         silent) apart from "I'm still current but got aborted some other
         way" (ref still points at me — e.g. some other externally-triggered
         abort — which still has to resolve `loading` since nothing else will). */
      const isCurrent = () => controllerRef.current === controller;

      /* allSettled, NOT all: the two indexes fail independently, and a support
         outage must not blank the product suggestions the reader came for. */
      Promise.allSettled([
        requestSuggestions(suggestBase, PRODUCT, term, controller.signal),
        requestSuggestions(suggestBase, SUPPORT, term, controller.signal)
      ])
        .then(([product, support]) => {
          const aborted = [product, support].some(
            leg => leg.status === "rejected" && (leg.reason as Error)?.name === "AbortError"
          );
          if (aborted) {
            throw Object.assign(new Error("aborted"), { name: "AbortError" });
          }
          /* Support is resolved first only to size the product cap. Products
             still render first so the group order is stable whichever leg
             settles first — SearchPanel opens a new section on every change of
             `group`, so an unstable order would draw duplicate headers. */
          const supportResults = support.status === "fulfilled" ?
            toSupportResults(support.value, MAX_SUPPORT_SUGGESTIONS) :
            [];
          const productResults = product.status === "fulfilled" ?
            toProductResults(product.value, MAX_SUGGESTIONS - supportResults.length) :
            [];
          const answer = [...productResults, ...supportResults];

          const partial = product.status === "rejected" || support.status === "rejected";
          /* Both down, or one down and nothing from the other, is not "nothing
             matched": it would show the empty message and report a zero. */
          if (partial && answer.length === 0) {
            const failed = (product.status === "rejected" ? product.reason : support.reason) as
              Error & { status?: string };
            failedStatus = failed?.status;
            throw new Error("both suggesters unusable");
          }
          return answer;
        })
        .then(answer => {
          if (!isCurrent()) {
            return;
          }
          setResults(answer);
          setLoading(false);
        })
        .catch((cause: unknown) => {
          if (!isCurrent()) {
            return;
          }
          lastTermRef.current = undefined;
          if ((cause as Error)?.name === "AbortError") {
            /* Not a failure the reader should see, but still the last word
               on this attempt — clear the spinner rather than leaving it
               stuck true. */
            setLoading(false);
            return;
          }
          setResults(undefined);
          setError(true);
          setErrorCode(failedStatus ?? "unknown");
          setLoading(false);
        });
    },
    [suggestBase]
  );

  return {
    results,
    loading,
    error,
    errorCode,
    onQueryChange
  };
}
